import React, { useCallback, useEffect, useEffectEvent, useRef } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { Task } from "@common/types/task";
import { useUpdateTask } from "@api/task.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { useToaster } from "@design-system/ToastMessages/useToaster";

const editTaskTitleFormSchema = z.object({
    title: z.string().trim().min(1, "Title cannot be empty"),
});

type EditTaskTitleFormValues = z.infer<typeof editTaskTitleFormSchema>;

const fields = createFormikFields<EditTaskTitleFormValues>();

interface Props {
    show: boolean;
    task: Task;
    onClose(): void;
    /** Runs once the close animation ends, e.g. to unmount the modal. */
    onExited?: () => void;
}

export const EditTaskTitleModal: React.FC<Props> = ({
    show,
    task,
    onClose,
    onExited,
}) => {
    const updateTask = useUpdateTask(task);
    const { error: errorToast } = useToaster();
    const inputRef = useRef<HTMLInputElement>(null);

    const onSubmit = useCallback(
        async (values: EditTaskTitleFormValues) => {
            const trimmedTitle = values.title.trim();
            if (trimmedTitle !== task.title) {
                await updateTask.mutateAsync({ title: trimmedTitle });
            }
            onClose();
        },
        [task.title, updateTask, onClose],
    );

    const formik = useFormik<EditTaskTitleFormValues>({
        initialValues: {
            title: task.title,
        },
        validate: validateFormikWithZodSchema(editTaskTitleFormSchema),
        validateOnMount: true,
        onSubmit: submitHelper({
            handler: onSubmit,
            failureHandler: async () => {
                errorToast("Failed to update task title");
            },
            loading: updateTask.isPending,
        }),
    });

    const isLoading = updateTask.isPending;

    // Reinitialize from the task only when the modal opens, so a live
    // refetch while it's open doesn't overwrite unsaved input.
    const resetTitleFromTask = useEffectEvent(() => {
        formik.resetForm({ values: { title: task.title } });
    });

    useEffect(() => {
        if (!show) {
            return;
        }

        resetTitleFromTask();

        const timer = setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 100);
        return () => clearTimeout(timer);
    }, [show]);

    return (
        <PlainModal show={show} onHide={onClose} onExited={onExited}>
            <StandardModalHeader>Edit task title</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            formik.handleSubmit();
                        }
                    }}
                >
                    <VStack gap={16}>
                        <fields.Text
                            name="title"
                            label="Title"
                            size="xl"
                            ref={inputRef}
                            placeholder="Enter task title..."
                        />
                        <HStack as="footer" justify="end" gap={12}>
                            <Button
                                variant="secondary"
                                size="xl"
                                onClick={onClose}
                                disabled={formik.isSubmitting || isLoading}
                            >
                                Cancel
                            </Button>

                            <ButtonWithLoader
                                disabled={!formik.isValid}
                                loading={formik.isSubmitting || isLoading}
                                variant="primary"
                                type="submit"
                                size="xl"
                            >
                                Save
                            </ButtonWithLoader>
                        </HStack>
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
